import { Builder } from '@builder.io/react';
import React, { useEffect, useState } from 'react';

import getAssetSource from '../../../app/Search/utils/getAssetSource';
import { fetchBuilderDataFromModel } from '../../../common/utils';
import { ProgressiveImage } from '../../../storywind';
import { Button } from '../../storyblocks-design-system/components/atoms/Button/Button';
import withBuilderDefaults from '../../utils/withBuilderDefaults';

const CircleImage = ({ src, alt, className = '' }) =>
  src ? (
    <div
      className={`bg-gray-100 w-32 h-32 absolute rounded-full overflow-hidden ${className} border-4 border-white`}
    >
      <ProgressiveImage
        src={getAssetSource(src)}
        alt={alt}
        className="w-full h-full object-cover"
      />
    </div>
  ) : null;

interface ExploreMusicLibraryBuilderData {
  title: string;
  description: string;
  cta: {
    buttonText: string;
    buttonHref: string;
  };
}

interface ExploreMusicLibraryProps {
  urlSlug?: string;
  modelName?: string;
  shouldOpenSideMenu?: boolean;
  title?: string;
  description?: string;
  buttonText?: string;
  buttonHref?: string;
  mainImage?: string;
  mainImageAlt?: string;
  firstImage?: string;
  firstImageAlt?: string;
  secondImage?: string;
  secondImageAlt?: string;
  thirdImage?: string;
  thirdImageAlt?: string;
}

// Design content, used for whatever the Builder entry leaves empty.
const EXPLORE_MUSIC_LIBRARY_DEFAULTS: ExploreMusicLibraryBuilderData = {
  title: '100% exclusive music tracks from award-winning artists',
  description:
    'Create content that stands out with unlimited access to 33,000+ royalty free music tracks including our Storyblocks Label collection.',
  cta: { buttonText: 'Explore Music Library', buttonHref: '/music' },
};

const ExploreMusicLibrary = ({
  urlSlug = '',
  modelName = 'landing-search-page',
  shouldOpenSideMenu,
  title,
  buttonText,
  buttonHref,
  description,
  mainImage = '/explore-music/logo.png',
  mainImageAlt = '',
  firstImage = '/explore-music/image-1.png',
  firstImageAlt = '',
  secondImage = '/explore-music/image-2.png',
  secondImageAlt = '',
  thirdImage = '/explore-music/image-3.png',
  thirdImageAlt = '',
}: ExploreMusicLibraryProps) => {
  const [builderData, setBuilderData] =
    useState<ExploreMusicLibraryBuilderData>(null); // From builder

  useEffect(() => {
    const builderOptions = {
      query: {
        'data.urlSlug': urlSlug,
      },
      options: {
        fields: 'data',
      },
    };
    const fetchBuilderData = async () => {
      const data = await fetchBuilderDataFromModel(modelName, builderOptions);
      // We fixed the typo (ExploreMediaLibrary -> ExploreMusicLibrary), but the
      // structured data field is still named `exploreMediaLibrary`. Rename this
      // once it is renamed in Builder, not before.
      if (data && data.exploreMediaLibrary) {
        const builderData = {
          title: data.exploreMediaLibrary.title,
          description: data.exploreMediaLibrary.description,
          cta: data.exploreMediaLibrary.cta,
        };
        setBuilderData(builderData);
      }
    };
    if (urlSlug) {
      fetchBuilderData();
    }
  }, [urlSlug, modelName]);

  // Precedence: Builder content, then whatever the caller passed as props,
  // then the design defaults.
  const content = withBuilderDefaults(
    builderData,
    withBuilderDefaults(
      { title, description, cta: { buttonText, buttonHref } },
      EXPLORE_MUSIC_LIBRARY_DEFAULTS
    )
  );

  return (
    <div
      className={`flex flex-col lg:flex-row w-full max-w-5xl mx-auto  px-6 md:px-12 lg:px-24 gap-y-12 relative ${
        shouldOpenSideMenu ? 'lg:gap-x-8' : 'lg:gap-x-8 2xl:gap-x-138'
      }`}
    >
      <div className="my-2 lg:flex-1">
        <div className="w-full h-120 flex justify-center items-center">
          <div className="w-350px h-350px bg-gray-900 rounded-3xl relative">
            <CircleImage
              src={firstImage}
              alt={firstImageAlt}
              className="-right-16 -top-16"
            />
            <CircleImage
              src={secondImage}
              alt={secondImageAlt}
              className="-bottom-16 -right-8"
            />
            <CircleImage
              src={thirdImage}
              alt={thirdImageAlt}
              className="bottom-8 -left-16"
            />
            <ProgressiveImage
              src={getAssetSource(mainImage)}
              alt={mainImageAlt}
              className="absolute w-2/3 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-2/3"
            />
          </div>
        </div>
      </div>
      <div className="my-2 lg:flex-1">
        <div className="w-full h-120 flex justify-center items-center">
          <div className="h-350px flex flex-col justify-center items-start">
            <p className="text-3xl font-bold">{content.title}</p>
            <p className="text-base">{content.description}</p>
            <Button
              href={content.cta.buttonHref}
              variant="secondary"
              size="large"
              className="mt-4"
            >
              {content.cta.buttonText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreMusicLibrary;
